import React, { memo, useContext } from 'react';
import type { CopyBlock as CopyBlockType } from '@segmently/schema-models/v2';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { SemanticTextTag } from '@modules/screen-view-v2/utils/semanticMarkup';
import { ArticleRenderContext } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { articleRoleStyle } from '@modules/screen-view-v2/utils/articlePresentationCss';
import { useElementAnimation, mergeAnimationClassName } from '../../../animations';
import { Text } from '../Text';
import styles from './CopyBlock.module.css';

export interface CopyBlockProps {
  copyBlock: CopyBlockType | undefined;
  locale: string;
  variables?: TemplateVariables;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  /** Semantic element for the title (articles only). Absent = div. */
  titleAs?: SemanticTextTag;
  /** Semantic element for the subtitle (articles only). Absent = div. */
  subtitleAs?: SemanticTextTag;
  /** Heading id of the title / subtitle (articles only, G2 D5). */
  titleId?: string;
  subtitleId?: string;
}

export const CopyBlock = memo(function CopyBlock({
  copyBlock,
  locale,
  variables,
  className,
  'data-testid': testId,
  editPath,
  titleAs,
  subtitleAs,
  titleId,
  subtitleId,
}: CopyBlockProps): React.ReactElement | null {
  // Article template (G2, D4): titleRole/subtitleRole → role variables; null context → no role style.
  const article = useContext(ArticleRenderContext);
  // Block-level animation only — title/subtitle refs are self-applied by Text
  const blockAnim = useElementAnimation(editPath);

  if (!copyBlock) {
    return null;
  }

  const { title, subtitle } = copyBlock;

  // Return null if all fields are missing
  if (!title && !subtitle) {
    return null;
  }

  const titleRoleStyle = articleRoleStyle(article?.presentation, copyBlock.titleRole);
  const subtitleRoleStyle = articleRoleStyle(article?.presentation, copyBlock.subtitleRole);
  const baseClassName = className ? `${styles.copyBlock} ${className}` : styles.copyBlock;

  return (
    <div
      className={mergeAnimationClassName(baseClassName, blockAnim)}
      style={blockAnim.style}
      data-anim={blockAnim['data-anim']}
      data-testid={testId}
      data-edit-path={editPath}
    >
      {title && (
        <Text
          label={title}
          locale={locale}
          variables={variables}
          editPath={editPath ? `${editPath}.title` : undefined}
          as={titleAs}
          roleStyle={titleRoleStyle}
          id={titleId}
        />
      )}
      {subtitle && (
        <Text
          label={subtitle}
          locale={locale}
          variables={variables}
          editPath={editPath ? `${editPath}.subtitle` : undefined}
          as={subtitleAs}
          roleStyle={subtitleRoleStyle}
          id={subtitleId}
        />
      )}
    </div>
  );
});

CopyBlock.displayName = 'CopyBlock';
